// src/pages/vendor/Profile.jsx - UPDATED WITH PAYOUT METHODS
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Badge,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Store,
  Email,
  Phone,
  LocationOn,
  Star,
  ShoppingBag,
  Verified,
  AccountBalance,
  Warning,
  AccountBalanceWallet,
  Smartphone,
  CreditCard,
  Payment,
  CheckCircle,
  Business,
  PhoneAndroid,
  AccountBalance as BankIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { vendorApi } from "../../api/vendorApi";
import { getUserAvatarUrl } from "../../utils/imageUtils";

// Ethiopian Banks List
const ETHIOPIAN_BANKS = [
  { code: "CBET", name: "Commercial Bank of Ethiopia" },
  { code: "BOA", name: "Bank of Abyssinia" },
  { code: "AWASH", name: "Awash Bank" },
  { code: "DASHEN", name: "Dashen Bank" },
  { code: "WEGAGEN", name: "Wegagen Bank" },
  { code: "UNITED", name: "United Bank" },
  { code: "NIB", name: "NIB International Bank" },
  { code: "ZEMEN", name: "Zemen Bank" },
  { code: "OROMIA", name: "Oromia Bank" },
  { code: "BERHAN", name: "Berhan Bank" },
  { code: "ABAY", name: "Abay Bank" },
  { code: "AMHARA", name: "Amhara Bank" },
  { code: "ADDIS", name: "Addis International Bank" },
  { code: "DEBUB", name: "Debub Global Bank" },
  { code: "BUNNA", name: "Bunna Bank" },
  { code: "ENAT", name: "Enat Bank" },
  { code: "TSEDEY", name: "Tsedey Bank" },
  { code: "SIINQEE", name: "Siinqee Bank" },
  { code: "GOH", name: "Goh Betoch Bank" },
];

// Payout Payment Methods - Chapa supported methods for vendor payouts
const PAYOUT_METHODS = [
  { 
    id: 'bank_transfer', 
    name: 'Bank Transfer', 
    icon: <BankIcon />, 
    description: 'Receive payout via bank transfer',
    requiresBankDetails: true,
    requiresPhone: false,
    chapaCode: 'bank_transfer',
  },
  { 
    id: 'telebirr', 
    name: 'TeleBirr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via TeleBirr mobile money',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'telebirr',
  },
  { 
    id: 'mpesa', 
    name: 'M-Pesa', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via M-Pesa mobile money',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'mpesa',
  },
  { 
    id: 'cbebirr', 
    name: 'CBE Birr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via CBE Birr digital payment',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'cbebirr',
  },
  { 
    id: 'awashbirr', 
    name: 'Awash Birr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via Awash Birr wallet',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'awashbirr',
  },
  { 
    id: 'ebirr', 
    name: 'eBirr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via eBirr digital payment',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'ebirr',
  },
  { 
    id: 'yaya', 
    name: 'Yaya Wallet', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via Yaya Wallet',
    requiresBankDetails: false,
    requiresPhone: true,
    chapaCode: 'yaya',
  },
  { 
    id: 'coopay', 
    name: 'Coopay', 
    icon: <Business />, 
    description: 'Receive payout via Coopay eBirr',
    requiresBankDetails: true,
    requiresPhone: false,
    chapaCode: 'coopay',
  },
  { 
    id: 'enat_bank', 
    name: 'Enat Bank', 
    icon: <Business />, 
    description: 'Receive payout via Enat Bank transfer',
    requiresBankDetails: true,
    requiresPhone: false,
    chapaCode: 'enat_bank',
  },
];

// Helper functions
const getBankNameFromCode = (code) => {
  const bank = ETHIOPIAN_BANKS.find(b => b.code === code);
  return bank ? bank.name : code || "";
};

const getBankCodeFromName = (name) => {
  const bank = ETHIOPIAN_BANKS.find(b => b.name === name);
  return bank ? bank.code : name || "";
};

const VendorProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(true);
  const [payoutBanks, setPayoutBanks] = useState([]);
  const [payoutWallets, setPayoutWallets] = useState([]);
  const [payoutMethodsLoading, setPayoutMethodsLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    storeName: "",
    storeDescription: "",
    businessEmail: "",
    businessPhone: "",
    businessAddress: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
    payoutMethod: "bank_transfer",
    payoutPhoneNumber: "",
  });

  const userAvatarUrl = getUserAvatarUrl(user);
  const displayAvatarUrl = previewImage || userAvatarUrl;

  useEffect(() => {
    let mounted = true;
    const loadPayoutMethods = async () => {
      setPayoutMethodsLoading(true);
      try {
        const response = await vendorApi.getPayoutMethods();
        if (mounted && response?.success) {
          setPayoutBanks(response.data?.banks || []);
          setPayoutWallets(response.data?.wallets || []);
        }
      } catch (error) {
        if (mounted) setError(error.response?.data?.message || 'Unable to load Chapa payout methods');
      } finally {
        if (mounted) setPayoutMethodsLoading(false);
      }
    };
    loadPayoutMethods();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (user) {
      const bankName = user.bankName || "";
      const displayBankName = getBankNameFromCode(bankName) || bankName;
      
      setProfileData({
        storeName: user.storeName || "",
        storeDescription: user.storeDescription || "",
        businessEmail: user.businessEmail || user.email || "",
        businessPhone: user.businessPhone || "",
        businessAddress: user.businessAddress || "",
        bankAccountName: user.bankAccountName || "",
        bankAccountNumber: user.bankAccountNumber || "",
        bankName: bankName,
        payoutMethod: user.payoutMethod || "bank_transfer",
        payoutPhoneNumber: user.payoutPhoneNumber || "",
      });

      // Check if selected method requires bank details
      const method = PAYOUT_METHODS.find(m => m.id === (user.payoutMethod || "bank_transfer"));
      setShowBankDetails(method ? method.requiresBankDetails : true);
    }
  }, [user]);

  const handleChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePayoutMethodChange = (e) => {
    const methodId = e.target.value;
    const method = PAYOUT_METHODS.find(m => m.id === methodId);
    
    setProfileData({
      ...profileData,
      payoutMethod: methodId,
    });

    // Show/hide bank details based on selected method
    if (method) {
      setShowBankDetails(method.requiresBankDetails);

      // Keep the selected payout method and the actual Chapa bank/wallet ID
      // synchronized. This prevents, for example, a TeleBirr method from
      // accidentally being sent with an M-Pesa bank code.
      if (method.requiresPhone) {
        const wallet = payoutWallets.find((item) =>
          String(item.name || '').toLowerCase().includes(String(method.name || '').toLowerCase()) ||
          String(method.name || '').toLowerCase().includes(String(item.name || '').toLowerCase())
        );
        if (wallet) {
          setProfileData((prev) => ({ ...prev, payoutMethod: methodId, bankName: String(wallet.id) }));
        }
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('storeLogo', selectedFile);
      
      const response = await vendorApi.updateStoreLogo(formData);
      if (response && response.vendor) {
        updateUser(response.vendor);
      }
      setOpenImageDialog(false);
      setSelectedFile(null);
      setPreviewImage(null);
      setSuccess('Store logo updated successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const selectedBank = [...payoutBanks, ...payoutWallets].find(
        (bank) => String(bank.id) === String(profileData.bankName) || bank.name === profileData.bankName
      );
      const chapaBankCode = selectedBank ? String(selectedBank.id) : getBankCodeFromName(profileData.bankName);

      const saveData = {
        storeName: profileData.storeName,
        storeDescription: profileData.storeDescription,
        businessEmail: profileData.businessEmail,
        businessPhone: profileData.businessPhone,
        businessAddress: profileData.businessAddress,
        bankAccountName: profileData.bankAccountName,
        bankAccountNumber: profileData.bankAccountNumber,
        bankName: chapaBankCode,
        payoutMethod: profileData.payoutMethod,
        payoutPhoneNumber: profileData.payoutPhoneNumber,
      };
      
      const response = await vendorApi.updateProfile(saveData);
      
      if (response && response.vendor) {
        updateUser(response.vendor);
      }
      
      setEditing(false);
      setSuccess("Profile updated successfully!");
      
      const updatedProfile = await vendorApi.getProfile();
      if (updatedProfile && updatedProfile.vendor) {
        updateUser(updatedProfile.vendor);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const bankName = user.bankName || "";
    const displayBankName = getBankNameFromCode(bankName) || bankName;
    
    setProfileData({
      storeName: user.storeName || "",
      storeDescription: user.storeDescription || "",
      businessEmail: user.businessEmail || user.email || "",
      businessPhone: user.businessPhone || "",
      businessAddress: user.businessAddress || "",
      bankAccountName: user.bankAccountName || "",
      bankAccountNumber: user.bankAccountNumber || "",
      bankName: bankName,
      payoutMethod: user.payoutMethod || "bank_transfer",
      payoutPhoneNumber: user.payoutPhoneNumber || "",
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const getAvatarLetter = () => {
    if (user?.storeName) return user.storeName[0].toUpperCase();
    return 'S';
  };

  const getDisplayBankName = () => {
    const bankName = user?.bankName || "";
    const selected = [...payoutBanks, ...payoutWallets].find((bank) => String(bank.id) === String(bankName));
    return selected?.name || getBankNameFromCode(bankName) || bankName || "Not set";
  };

  const getPayoutMethodInfo = (methodId) => {
    return PAYOUT_METHODS.find(m => m.id === methodId) || PAYOUT_METHODS[0];
  };

  const getPayoutMethodName = (methodId) => {
    const method = getPayoutMethodInfo(methodId);
    return method ? method.name : "Bank Transfer";
  };

  const getSelectedMethod = () => {
    return getPayoutMethodInfo(profileData.payoutMethod);
  };

  const getSelectedMethodRequiresBank = () => {
    const method = getSelectedMethod();
    return method ? method.requiresBankDetails : true;
  };

  const getSelectedMethodRequiresPhone = () => {
    const method = getSelectedMethod();
    return method ? method.requiresPhone : false;
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedMethod = getSelectedMethod();

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Store Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your store information and payout preferences
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        borderRadius: 3, 
        textAlign: "center",
      }}>
        <Stack alignItems="center" spacing={2}>
          <Box sx={{ position: "relative" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={() => setOpenImageDialog(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <PhotoCamera sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </IconButton>
              }
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  bgcolor: displayAvatarUrl ? 'transparent' : 'primary.main',
                  fontSize: { xs: 36, sm: 48 },
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: theme.shadows[3],
                }}
                src={displayAvatarUrl}
              >
                {!displayAvatarUrl && getAvatarLetter()}
              </Avatar>
            </Badge>
          </Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            {user.storeName || "Store Name"}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Chip icon={<Star sx={{ color: "#ffc107" }} />} label={`${user.rating || 0} Rating`} />
            <Chip icon={<ShoppingBag />} label={`${user.totalSales || 0} Sales`} />
            {user.isVerified && <Chip icon={<Verified />} label="Verified" color="success" />}
            {!user.isVerified && <Chip icon={<Warning />} label="Pending Verification" color="warning" variant="outlined" />}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Store Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                Store Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Store Name
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeName"
                      value={profileData.storeName}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight="500">
                      {user.storeName || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Description
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeDescription"
                      value={profileData.storeDescription}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {user.storeDescription || "No description provided"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Business Email
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessEmail"
                      value={profileData.businessEmail}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessEmail || user.email || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Phone
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessPhone"
                      value={profileData.businessPhone}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessPhone || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Address
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessAddress"
                      value={profileData.businessAddress}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessAddress || "Not set"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Payout Payment Method */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Payment sx={{ mr: 1, verticalAlign: "middle" }} />
                Payout Preferences
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Select how you want to receive your payouts when admin approves your withdrawal request
              </Typography>

              {editing ? (
                <>
                  <RadioGroup
                    name="payoutMethod"
                    value={profileData.payoutMethod}
                    onChange={handlePayoutMethodChange}
                  >
                    <Grid container spacing={1}>
                      {PAYOUT_METHODS.map((method) => (
                        <Grid item xs={12} sm={6} md={4} key={method.id}>
                          <Paper
                            variant={profileData.payoutMethod === method.id ? "outlined" : "outlined"}
                            sx={{
                              p: 1.5,
                              borderColor: profileData.payoutMethod === method.id ? 'primary.main' : 'grey.300',
                              borderWidth: profileData.payoutMethod === method.id ? 2 : 1,
                              bgcolor: profileData.payoutMethod === method.id ? 'primary.light' : 'transparent',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: profileData.payoutMethod === method.id ? 'primary.light' : 'grey.50',
                              },
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => {
                              setProfileData(prev => ({ ...prev, payoutMethod: method.id }));
                              setShowBankDetails(method.requiresBankDetails);
                              if (method.requiresPhone) {
                                const wallet = payoutWallets.find((item) =>
                                  String(item.name || '').toLowerCase().includes(String(method.name || '').toLowerCase()) ||
                                  String(method.name || '').toLowerCase().includes(String(item.name || '').toLowerCase())
                                );
                                if (wallet) {
                                  setProfileData(prev => ({ ...prev, payoutMethod: method.id, bankName: String(wallet.id) }));
                                }
                              }
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box sx={{ color: profileData.payoutMethod === method.id ? 'primary.main' : 'text.secondary' }}>
                                {method.icon}
                              </Box>
                              <Box flex={1}>
                                <Typography variant="body2" fontWeight={500}>
                                  {method.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {method.description}
                                </Typography>
                              </Box>
                              <Radio
                                checked={profileData.payoutMethod === method.id}
                                onChange={() => {}}
                                value={method.id}
                                name="payoutMethod"
                                size="small"
                              />
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>

                  {/* Chapa wallet/bank destination */}
                  {getSelectedMethodRequiresPhone() ? (
                    <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                      <InputLabel>Mobile Wallet</InputLabel>
                      <Select
                        name="bankName"
                        value={profileData.bankName || ""}
                        label="Mobile Wallet"
                        onChange={(e) => {
                          const walletId = e.target.value;
                          const wallet = payoutWallets.find((item) => String(item.id) === String(walletId));
                          const matchedMethod = PAYOUT_METHODS.find((method) =>
                            method.requiresPhone && (
                              String(wallet?.name || '').toLowerCase().includes(String(method.name || '').toLowerCase()) ||
                              String(method.name || '').toLowerCase().includes(String(wallet?.name || '').toLowerCase())
                            )
                          );
                          setProfileData((prev) => ({
                            ...prev,
                            bankName: walletId,
                            payoutMethod: matchedMethod?.id || prev.payoutMethod,
                          }));
                        }}
                        disabled={payoutMethodsLoading}
                      >
                        {payoutWallets.map((wallet) => (
                          <MenuItem key={wallet.id} value={String(wallet.id)}>
                            {wallet.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Select the wallet provider supported by your Chapa account.
                      </FormHelperText>
                    </FormControl>
                  ) : null}

                  {/* Conditional Phone Number Field */}
                  <Collapse in={getSelectedMethodRequiresPhone()}>
                    <TextField
                      fullWidth
                      name="payoutPhoneNumber"
                      label="Mobile Number for Payout"
                      value={profileData.payoutPhoneNumber}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 2 }}
                      placeholder="e.g., 0912345678"
                      helperText="Required for mobile money payouts (TeleBirr, M-Pesa, CBE Birr, etc.)"
                    />
                  </Collapse>

                  {/* Payout destination details */}
                  <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }}>
                        <Chip label="Bank Details" size="small" />
                      </Divider>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        {getSelectedMethodRequiresPhone()
                          ? 'Mobile wallet details required for this payout method'
                          : `Bank details required for ${getPayoutMethodName(profileData.payoutMethod)} payouts`}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Account Holder Name
                            </Typography>
                            <TextField
                              fullWidth
                              name="bankAccountName"
                              value={profileData.bankAccountName}
                              onChange={handleChange}
                              size="small"
                              sx={{ mt: 0.5 }}
                              placeholder="e.g., John Doe"
                            />
                          </Box>
                        </Grid>
                        {showBankDetails && (
                          <Grid item xs={12} sm={6}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Account Number
                              </Typography>
                              <TextField
                                fullWidth
                                name="bankAccountNumber"
                                value={profileData.bankAccountNumber}
                                onChange={handleChange}
                                size="small"
                                sx={{ mt: 0.5 }}
                                placeholder="e.g., 1000123456789"
                              />
                            </Box>
                          </Grid>
                        )}
                        {showBankDetails && (
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Bank Name
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                              <Select
                                name="bankName"
                                value={profileData.bankName}
                                onChange={handleChange}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em style={{ color: 'text.secondary' }}>Select Bank</em>
                                </MenuItem>
                                {payoutBanks.map((bank) => (
                                  <MenuItem key={bank.id} value={String(bank.id)}>
                                    {bank.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>Select your bank from the list</FormHelperText>
                            </FormControl>
                          </Box>
                        </Grid>
                        )}
                      </Grid>
                    </Box>
                </>
              ) : (
                <Stack spacing={2}>
                  {/* Selected Method Display */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'success.light',
                      borderColor: 'success.main',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ fontSize: 32 }}>
                        {selectedMethod?.icon || <BankIcon />}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {getPayoutMethodName(profileData.payoutMethod)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedMethod?.description || 'Default payout method'}
                        </Typography>
                        {user.payoutPhoneNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone: {user.payoutPhoneNumber}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        icon={<CheckCircle />}
                        label="Selected"
                        color="success"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Stack>
                  </Paper>

                  {/* Show Bank Details if selected method requires it */}
                  {getSelectedMethodRequiresBank() && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        <AccountBalanceWallet sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                        Bank Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Account Holder</Typography>
                          <Typography variant="body2">{user.bankAccountName || "Not set"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Account Number</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {user.bankAccountNumber || "Not set"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                          <Typography variant="body2">{getDisplayBankName()}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Show Phone Number if selected method requires it */}
                  {getSelectedMethodRequiresPhone() && user.payoutPhoneNumber && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        <PhoneAndroid sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                        Mobile Number
                      </Typography>
                      <Typography variant="body2">{user.payoutPhoneNumber}</Typography>
                    </Paper>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Store Statistics */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Store Statistics
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalProducts || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Products</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalOrders || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Orders</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalCustomers || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Customers</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.commissionRate || 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Commission Rate</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Update Logo Dialog */}
      <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Store Logo</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Avatar
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                mx: 'auto',
                mb: 2,
                bgcolor: 'primary.main',
              }}
              src={displayAvatarUrl}
            >
              {!displayAvatarUrl && getAvatarLetter()}
            </Avatar>
            <Button variant="outlined" component="label">
              Choose New Logo
              <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenImageDialog(false)} fullWidth={isMobile}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadLogo}
            disabled={!selectedFile || uploading}
            fullWidth={isMobile}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorProfile;